/**
 * Extracts the driver's name from a chat name that follows the pattern:
 * "<side> <plate> <driver first> <driver last>"
 * e.g. "C232M PY24556 Viktor Synzhalov" → "Viktor Synzhalov"
 *
 * A chat with no driver name (e.g. "C132 GDA67563") has nothing to return.
 */
export const extractDriverNameFromChatName = (
  chatName: string,
): string | undefined => {
  const parts = chatName.trim().split(/\s+/)

  // Need at least 3 parts: side, plate, and one name part
  if (parts.length < 3) {
    return undefined
  }

  // Skip first two parts (side and plate), take the rest as name
  return parts.slice(2).join(" ") || undefined
}

/**
 * Extracts driver initials from a chat name that follows the pattern:
 * "<side> <plate> <driver first> <driver last>"
 * e.g. "C232M PY24556 Viktor Synzhalov" → "VS"
 *
 * Edge cases handled:
 * - No driver name (e.g. "C132 GDA67563") → undefined
 * - Single-word name → single initial
 * - Diacritics → preserved
 */
export const extractDriverInitialsFromChatName = (
  chatName: string,
): string | undefined => {
  const driverName = extractDriverNameFromChatName(chatName)

  if (!driverName) {
    return undefined
  }

  // Take first char of each name part, up to 2 parts for initials
  const initials = driverName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("")

  return initials || undefined
}
